import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { ErrorResponse, Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { CheckCallStatus } from '@/model';

import { useCurrentUserStore } from './CurrentUser';
import { useParentEntityStore } from './ParentEntity';

export interface SendCheckCallRemindersPayload {
  emails: string;
  confirmCompletion: () => void;
}

export interface PerformLoadOptions {
  bypassCache: boolean;
}

export interface CheckCallStore {
  cancelCheckCallEmails: (shipmentId: number) => Promise<void>;
  checkCallStatus: Ref<Loadable<CheckCallStatus>>;
  loadCheckCallStatus: (
    shipmentId: number,
    opts?: PerformLoadOptions,
  ) => Promise<void>;
  sendCheckCallEmails: (
    shipmentId: number,
    payload: SendCheckCallRemindersPayload,
  ) => Promise<void>;
}

const PERFORM_LOAD_DEFAULT_OPTIONS: PerformLoadOptions = {
  bypassCache: false,
};

const _useCheckCallStore = defineStore('checkCall', (): CheckCallStore => {
  const { selectedDomainId } = useCurrentUserStore();
  const { t } = useI18n();
  const { parentEntity } = useParentEntityStore();
  const toast = useToast();

  const checkCallStatus: Ref<Loadable<CheckCallStatus>> = ref({
    loading: false,
  });

  const loadCheckCallStatus = async (
    shipmentId: number,
    opts: PerformLoadOptions = PERFORM_LOAD_DEFAULT_OPTIONS,
  ) => {
    const options = { ...PERFORM_LOAD_DEFAULT_OPTIONS, ...opts };

    const isPresent = !!checkCallStatus.value.data;
    const isLoading = checkCallStatus.value.loading;
    const requiresLoad = !isPresent && !isLoading;

    if (options.bypassCache || requiresLoad) {
      return performLoad(shipmentId);
    }
  };

  const performLoad = async (shipmentId: number) => {
    try {
      checkCallStatus.value.loading = true;
      checkCallStatus.value.data =
        await api.shipmentCheckCallReminder.getCheckCallReminderStatus(
          shipmentId,
        );
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      checkCallStatus.value.error = e;
      console.error('Error loading check call reminder status', e);
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('common.paramStatuses', {
            param: t('carrierPortal.checkCallReminders.checkCallReminders', 2),
          }),
        }),
        error: e,
      });
    } finally {
      checkCallStatus.value.loading = false;
    }
  };

  const sendCheckCallEmails = async (
    shipmentId: number,
    { emails, confirmCompletion }: SendCheckCallRemindersPayload,
  ) => {
    try {
      checkCallStatus.value.data =
        await api.shipmentCheckCallReminder.sendReminderEmails(
          shipmentId,
          emails,
        );
      toast.addSuccess({
        detail: t('messages.successSendingParam', {
          param: t(
            'carrierPortal.checkCallReminders.checkCallReminders',
            emails.length,
          ),
        }),
      });
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      let errorDetail = '';
      if (axios.isAxiosError(e)) {
        const errorResponse = e.response?.data as ErrorResponse;
        errorDetail = errorResponse.exceptionMessage;
      }

      toast.addError({
        detail:
          t('errors.sendingParam', {
            param: t(
              'carrierPortal.checkCallReminders.checkCallReminders',
              emails.length,
            ),
          }) + (errorDetail ? ` - ${errorDetail}` : ''),
        error: e,
      });
    } finally {
      confirmCompletion();
    }
  };

  const cancelCheckCallEmails = async (shipmentId: number) => {
    try {
      checkCallStatus.value.data =
        await api.shipmentCheckCallReminder.cancelCheckCallReminders(
          shipmentId,
        );
      toast.addSuccess({
        detail: t('messages.successCancelingParam', {
          param: t('carrierPortal.checkCallReminders.checkCallReminders', 2),
        }),
      });
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      toast.addError({
        detail: t('shipments.errors.cancelingCheckCallEmails'),
        error: e,
      });
    }
  };

  const reset = () => {
    checkCallStatus.value = { loading: false };
  };

  watch(parentEntity, () => {
    reset();
  });

  watch(selectedDomainId, () => {
    reset();
  });

  return {
    cancelCheckCallEmails,
    checkCallStatus,
    loadCheckCallStatus,
    sendCheckCallEmails,
  };
});

export const useCheckCallStore = (): CheckCallStore => {
  const store = _useCheckCallStore();
  const { checkCallStatus } = storeToRefs(store);
  const { cancelCheckCallEmails, loadCheckCallStatus, sendCheckCallEmails } =
    store;

  return {
    cancelCheckCallEmails,
    checkCallStatus,
    loadCheckCallStatus,
    sendCheckCallEmails,
  };
};
